// Bridges stored page content and the section model used by the page editor.
//
// content_md may be (newest → oldest):
//   1. Section JSON  ({"sections":[...]})  — pages saved with the section editor.
//   2. Editor.js JSON ({"blocks":[...]})   — pages saved with the old block editor.
//   3. Legacy markdown                     — pages never re-saved in the portal.
//
// The editor always works on PageSection[]; older formats are converted on open
// (hero blocks become image-background sections, columns blocks become
// multi-column sections). Saving always produces section JSON.

import { READABLE_TEXT_MIN_RATIO, readableTextOn } from "@cms/ui/lib/readable-text";

type BlockData = Record<string, any>;

export type SectionWidth = "narrow" | "normal" | "wide";
export type SectionAlign = "left" | "center" | "right";

/**
 * Section vertical padding: a step on the template's spacing scale
 * (tokens.css --space-N, 0.25rem = 4px base). Legacy stored names
 * compact/default/extra normalize to 6/12/16 on load; 12 is the
 * .content-section default and emits no padding class.
 */
export const SECTION_PADDING_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 16, 24, 32, 48, 64] as const;
export type SectionPadding = (typeof SECTION_PADDING_STEPS)[number];
export const DEFAULT_SECTION_PADDING: SectionPadding = 12;

const LEGACY_PADDINGS: Record<string, SectionPadding> = { compact: 6, default: 12, extra: 16 };

export function normalizeSectionPadding(raw: unknown): SectionPadding {
  if (typeof raw === "string" && raw in LEGACY_PADDINGS) return LEGACY_PADDINGS[raw];
  return (SECTION_PADDING_STEPS as readonly number[]).includes(raw as number)
    ? (raw as SectionPadding)
    : DEFAULT_SECTION_PADDING;
}

/** The colour part of a background — layered over the image when both are set. */
export type SectionColorLayer =
  | { type: "none" }
  | { type: "color"; color: string }
  | { type: "gradient"; from: string; to: string };

/**
 * Section background: an optional image and an independent colour/gradient
 * layer that tints it (or stands alone). Older sections stored a single
 * `{ type: "none"|"color"|"gradient"|"image" }` union; normalizeBackground
 * migrates that shape on load and the template accepts both forever.
 */
export interface SectionBackground {
  color: SectionColorLayer;
  /** Stored R2 key of the background image ("" = none). */
  imageKey: string;
  /** Transient/public preview URL when known. */
  imageUrl?: string;
  /** Fixed (parallax-style) background image; only meaningful with imageKey. */
  fixed?: boolean;
  /** Readable text colour derived from the colour layer (see sectionTextColor).
   *  Persisted so the dependency-free template renderer can consume it without
   *  the contrast maths; recomputed on every save, ignoring any image. */
  textColor?: string;
}

export interface SectionSettings {
  columns: 1 | 2 | 3;
  background: SectionBackground;
  width: SectionWidth;
  align: SectionAlign;
  /** Stretch the section to the viewport height, content vertically centred. */
  fullHeight: boolean;
  /** Vertical padding scale. */
  padding: SectionPadding;
}

/** Per-column content alignment ("start" = left / top). */
export type ColumnAlign = "start" | "center" | "end";

export interface SectionColumn {
  blocks: BlockData[];
  /** Horizontal alignment; absent = inherit the section's align. */
  align?: ColumnAlign;
  /** Vertical alignment within the row; absent = top. */
  valign?: ColumnAlign;
}

/** Section type, fixed when the section is added. Absent in stored JSON = blank. */
export type SectionType = "blank" | "timeline";

export type TimelineLayout = "vertical" | "horizontal" | "zigzag";

export interface TimelineStep {
  /** Stable id for list keys / drag-reorder; opaque to the renderer. */
  id: string;
  /** Step body content — one block editor per step (headings, text, …). */
  blocks: BlockData[];
  /** Stored R2 key of the marker image ("" = numbered/dot marker). */
  imageKey: string;
  /** Transient/public preview URL when known. */
  imageUrl?: string;
}

export interface SectionTimeline {
  layout: TimelineLayout;
  steps: TimelineStep[];
  /** Show 1-based step numbers in the markers; false renders plain dots. */
  showNumbers: boolean;
  /** Marker circle / dot colour (validated); absent = theme accent. */
  markerColor?: string;
  /** Connecting line colour (validated); absent = theme border. */
  lineColor?: string;
  /** Optional block content rendered above the timeline. */
  above?: BlockData[];
  /** Optional block content rendered below the timeline. */
  below?: BlockData[];
}

export interface PageSection {
  id: string;
  type: SectionType;
  settings: SectionSettings;
  columns: SectionColumn[];
  /** Present when type === "timeline"; the columns are unused then. */
  timeline?: SectionTimeline;
}

// Colour-shaped values only: hex, or a single rgb()/rgba()/oklch() call whose
// body is a strict character whitelist. Shared with section-preview.ts (its
// preview) and mirrored by the template renderer (render-blocks.ts).
export const SAFE_COLOR_RE =
  /^(?:#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|(?:rgb|rgba|oklch)\([0-9a-zA-Z.,%\s/+-]{1,50}\))$/;

/** The value when it is a valid colour, else "". */
export function safeColor(value: unknown): string {
  return typeof value === "string" && SAFE_COLOR_RE.test(value) ? value : "";
}

/**
 * Readable text colour for a section, derived from the colour layer we set —
 * never from the image. A solid tint derives against itself; a gradient against
 * its first stop. "" when there is no colour layer (text keeps the site
 * default). The single source of truth for section text colour, shared by the
 * canvas, the column editor and the save path (which persists it as
 * background.textColor for the template renderer).
 */
export function sectionTextColor(background: SectionBackground): string {
  const { color } = background;
  if (color.type === "color" && safeColor(color.color)) {
    return readableTextOn(color.color, READABLE_TEXT_MIN_RATIO);
  }
  if (color.type === "gradient" && safeColor(color.from)) {
    return readableTextOn(color.from, READABLE_TEXT_MIN_RATIO);
  }
  return "";
}

const WIDTHS: SectionWidth[] = ["narrow", "normal", "wide"];
const ALIGNS: SectionAlign[] = ["left", "center", "right"];
const COLUMN_ALIGNS: ColumnAlign[] = ["start", "center", "end"];
const TIMELINE_LAYOUTS: TimelineLayout[] = ["vertical", "horizontal", "zigzag"];

function sectionId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `s-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function defaultSettings(): SectionSettings {
  return {
    columns: 1,
    background: { color: { type: "none" }, imageKey: "" },
    width: "normal",
    align: "left",
    fullHeight: false,
    padding: DEFAULT_SECTION_PADDING,
  };
}

/** A deep copy of a section under fresh ids (settings + column/step content). */
export function cloneSection(section: PageSection): PageSection {
  const copy: PageSection = JSON.parse(JSON.stringify(section));
  copy.id = sectionId();
  if (copy.timeline) {
    copy.timeline.steps = copy.timeline.steps.map((step) => ({ ...step, id: sectionId() }));
  }
  return copy;
}

/** A fresh, empty blank section (one empty column, default settings). */
export function newSection(settings: Partial<SectionSettings> = {}): PageSection {
  const merged = { ...defaultSettings(), ...settings };
  return {
    id: sectionId(),
    type: "blank",
    settings: merged,
    columns: Array.from({ length: merged.columns }, () => ({ blocks: [] })),
  };
}

/** A fresh step with no content (numbered marker until an image is chosen). */
export function newTimelineStep(): TimelineStep {
  return { id: sectionId(), blocks: [], imageKey: "" };
}

/** A fresh timeline section: vertical layout, numbered, three empty steps. */
export function newTimelineSection(): PageSection {
  return {
    ...newSection(),
    type: "timeline",
    timeline: {
      layout: "vertical",
      steps: [newTimelineStep(), newTimelineStep(), newTimelineStep()],
      showNumbers: true,
    },
  };
}

function normalizeColorLayer(raw: any): SectionColorLayer {
  if (!raw || typeof raw !== "object") return { type: "none" };
  if (raw.type === "color" && typeof raw.color === "string" && raw.color) {
    return { type: "color", color: raw.color };
  }
  if (raw.type === "gradient" && typeof raw.from === "string" && typeof raw.to === "string") {
    return { type: "gradient", from: raw.from, to: raw.to };
  }
  return { type: "none" };
}

export function normalizeBackground(raw: any): SectionBackground {
  if (!raw || typeof raw !== "object") {
    return { color: { type: "none" }, imageKey: "" };
  }

  // Legacy single-type union: exactly one of colour / gradient / image.
  if (typeof raw.type === "string") {
    if (raw.type === "image" && typeof raw.imageKey === "string" && raw.imageKey) {
      const bg: SectionBackground = { color: { type: "none" }, imageKey: raw.imageKey };
      if (typeof raw.imageUrl === "string" && raw.imageUrl) bg.imageUrl = raw.imageUrl;
      return bg;
    }
    return { color: normalizeColorLayer(raw), imageKey: "" };
  }

  // Current shape: independent colour layer + image.
  const bg: SectionBackground = {
    color: normalizeColorLayer(raw.color),
    imageKey: typeof raw.imageKey === "string" ? raw.imageKey : "",
  };
  if (bg.imageKey && typeof raw.imageUrl === "string" && raw.imageUrl) bg.imageUrl = raw.imageUrl;
  if (bg.imageKey && raw.fixed === true) bg.fixed = true;
  if (!bg.imageKey) delete bg.imageUrl;
  const textColor = safeColor(raw.textColor);
  if (textColor) bg.textColor = textColor;
  return bg;
}

/**
 * A step's body blocks: the current `blocks` array (migrated), or legacy
 * `title`/`description` folded into a heading + paragraph so pre-blocks
 * timelines keep their content when opened.
 */
function timelineStepBlocks(step: any): BlockData[] {
  if (Array.isArray(step?.blocks)) return step.blocks.map(migrateBlock);
  const blocks: BlockData[] = [];
  if (typeof step?.title === "string" && step.title) {
    blocks.push({ type: "header", data: { text: step.title, level: 3 } });
  }
  if (typeof step?.description === "string" && step.description) {
    blocks.push({ type: "paragraph", data: { text: step.description } });
  }
  return blocks;
}

function normalizeBlockList(raw: any): BlockData[] {
  return Array.isArray(raw) ? raw.map(migrateBlock) : [];
}

function normalizeTimeline(raw: any): SectionTimeline {
  const steps: TimelineStep[] = (Array.isArray(raw?.steps) ? raw.steps : [])
    .map((step: any): TimelineStep => {
      const imageKey = typeof step?.imageKey === "string" ? step.imageKey : "";
      return {
        id: typeof step?.id === "string" && step.id ? step.id : sectionId(),
        blocks: timelineStepBlocks(step),
        imageKey,
        ...(imageKey && typeof step?.imageUrl === "string" && step.imageUrl
          ? { imageUrl: step.imageUrl }
          : {}),
      };
    });
  const above = normalizeBlockList(raw?.above);
  const below = normalizeBlockList(raw?.below);
  return {
    layout: TIMELINE_LAYOUTS.includes(raw?.layout) ? raw.layout : "vertical",
    steps,
    showNumbers: raw?.showNumbers !== false,
    ...(safeColor(raw?.markerColor) ? { markerColor: raw.markerColor } : {}),
    ...(safeColor(raw?.lineColor) ? { lineColor: raw.lineColor } : {}),
    ...(above.length ? { above } : {}),
    ...(below.length ? { below } : {}),
  };
}

/**
 * Coerce a raw stored section into a valid PageSection: settings clamped to the
 * allowed values and the columns array reconciled with settings.columns (extra
 * column docs are merged into the last kept column so no content is lost).
 */
function normalizeSection(raw: any): PageSection {
  const rawSettings = raw?.settings ?? {};
  const parsed = parseInt(rawSettings.columns, 10);
  const columnCount = (Math.min(Math.max(Number.isNaN(parsed) ? 1 : parsed, 1), 3)) as 1 | 2 | 3;

  const settings: SectionSettings = {
    columns: columnCount,
    background: normalizeBackground(rawSettings.background),
    width: WIDTHS.includes(rawSettings.width) ? rawSettings.width : "normal",
    align: ALIGNS.includes(rawSettings.align) ? rawSettings.align : "left",
    fullHeight: rawSettings.fullHeight === true,
    padding: normalizeSectionPadding(rawSettings.padding),
  };

  const docs: SectionColumn[] = (Array.isArray(raw?.columns) ? raw.columns : [])
    .map((col: any): SectionColumn => ({
      blocks: Array.isArray(col?.blocks) ? col.blocks.map(migrateBlock) : [],
      ...(COLUMN_ALIGNS.includes(col?.align) ? { align: col.align as ColumnAlign } : {}),
      ...(COLUMN_ALIGNS.includes(col?.valign) ? { valign: col.valign as ColumnAlign } : {}),
    }));

  const type: SectionType = raw?.type === "timeline" ? "timeline" : "blank";

  return {
    id: typeof raw?.id === "string" && raw.id ? raw.id : sectionId(),
    type,
    settings,
    columns: fitColumns(docs, columnCount),
    ...(type === "timeline" ? { timeline: normalizeTimeline(raw?.timeline) } : {}),
  };
}

/**
 * Reconcile a columns array with a target count: extra column docs merge into
 * the last kept column (no content lost), missing ones pad out empty.
 */
export function fitColumns(columns: SectionColumn[], count: 1 | 2 | 3): SectionColumn[] {
  const docs = columns.map((col): SectionColumn => ({
    ...col,
    blocks: Array.isArray(col?.blocks) ? col.blocks : [],
  }));
  while (docs.length < count) docs.push({ blocks: [] });
  if (docs.length > count) {
    const overflow = docs.splice(count).flatMap((col) => col.blocks);
    docs[count - 1] = { ...docs[count - 1], blocks: [...docs[count - 1].blocks, ...overflow] };
  }
  return docs;
}

/**
 * Parse stored content_md into the section model, converting older formats:
 * block JSON is grouped into sections (hero → image-background section,
 * columns → multi-column section, runs of ordinary blocks → one-column
 * section) and legacy markdown becomes a single one-column section.
 */
export function contentToSections(value: string | null | undefined): PageSection[] {
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      const data = JSON.parse(value);
      if (data && Array.isArray(data.sections)) return data.sections.map(normalizeSection);
      if (data && Array.isArray(data.blocks)) return blocksToSections(data.blocks.map(migrateBlock));
    } catch {
      // Not valid JSON — fall through to markdown conversion.
    }
  }
  const blocks = markdownToBlocks(typeof value === "string" ? value : "");
  return blocks.length ? [sectionFromBlocks(blocks)] : [];
}

function sectionFromBlocks(blocks: BlockData[]): PageSection {
  const section = newSection();
  section.columns = [{ blocks }];
  return section;
}

function blocksToSections(blocks: BlockData[]): PageSection[] {
  const sections: PageSection[] = [];
  let buffer: BlockData[] = [];
  const flush = () => {
    if (buffer.length) {
      sections.push(sectionFromBlocks(buffer));
      buffer = [];
    }
  };

  for (const block of blocks) {
    if (block?.type === "hero") {
      flush();
      const d = block.data ?? {};
      const section = newSection({
        align: "center",
        background: { color: { type: "none" }, imageKey: typeof d.imageKey === "string" ? d.imageKey : "" },
      });
      section.columns = [{ blocks: Array.isArray(d.blocks) ? d.blocks : [] }];
      sections.push(section);
    } else if (block?.type === "columns") {
      flush();
      const cols: BlockData[] = Array.isArray(block.data?.columns) ? block.data.columns : [];
      const count = Math.min(Math.max(cols.length, 1), 3) as 1 | 2 | 3;
      const section = newSection({ columns: count });
      section.columns = cols.slice(0, 3).map((col) => ({
        blocks: Array.isArray(col?.blocks) ? col.blocks : [],
      }));
      while (section.columns.length < count) section.columns.push({ blocks: [] });
      sections.push(section);
    } else {
      buffer.push(block);
    }
  }
  flush();
  return sections;
}

/**
 * Serialize the section model to the string stored in content_md. An empty
 * editor serializes to an empty sections doc so the renderer treats it as
 * (empty) section content rather than markdown.
 */
export function sectionsToContent(sections: PageSection[]): string {
  const withDerived = (sections ?? []).map((section) => {
    // Recompute the derived text colour from the current colour layer so the
    // persisted value never drifts; strip it when there is no colour layer.
    const textColor = sectionTextColor(section.settings.background);
    const { textColor: _prev, ...rest } = section.settings.background;
    const background = textColor ? { ...rest, textColor } : rest;
    return { ...section, settings: { ...section.settings, background } };
  });
  return JSON.stringify({ sections: withDerived });
}

/**
 * Normalize legacy block shapes so pre-section pages keep their content when
 * converted (otherwise old hero/columns docs would load empty and be lost on
 * the next save).
 *
 * - Columns: the interim @calumk/editorjs-columns plugin stored `{ cols: [...] }`;
 *   the later custom block used `{ columns: [{ blocks }] }`.
 * - Hero: the old structured hero stored `{ heading, subheading, ctaText,
 *   ctaHref, imageKey }`; the later custom block stored `{ imageKey, blocks }`.
 */
function migrateBlock(block: BlockData): BlockData {
  if (!block?.data) return block;

  if (block.type === "columns") {
    if (Array.isArray(block.data.columns)) return block;
    if (Array.isArray(block.data.cols)) {
      return {
        ...block,
        data: {
          columns: block.data.cols.map((col: BlockData) => ({
            blocks: Array.isArray(col?.blocks) ? col.blocks : [],
          })),
        },
      };
    }
    return block;
  }

  if (block.type === "hero" && !Array.isArray(block.data.blocks)) {
    const d = block.data;
    const blocks: BlockData[] = [];
    if (d.heading) blocks.push({ type: "header", data: { text: d.heading, level: 2 } });
    if (d.subheading) blocks.push({ type: "paragraph", data: { text: d.subheading } });
    if (d.ctaText) blocks.push({ type: "cta", data: { label: d.ctaText, href: d.ctaHref || "" } });
    return { ...block, data: { imageKey: d.imageKey || "", blocks } };
  }

  return block;
}

function markdownToBlocks(md: string): BlockData[] {
  const text = (md || "").trim();
  if (!text) return [];

  return text
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const heading = chunk.match(/^(#{1,6})\s+(.*)$/);
      if (heading) {
        return { type: "header", data: { text: inlineMarkdown(heading[2]), level: Math.min(heading[1].length, 6) } };
      }
      return { type: "paragraph", data: { text: inlineMarkdown(chunk.replace(/\n+/g, " ")) } };
    });
}

/**
 * Minimal inline markdown → HTML (bold/italic/link) so converted legacy content
 * keeps its emphasis when opened in the editor.
 */
function inlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/\*(.+?)\*/g, "<i>$1</i>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
}
